import React, { useEffect, useRef, useState } from 'react';
import { Library, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PromptSuggestionsProps {
  suggestions: Array<{key: string, value: string}>;
  onSuggestionClick: (suggestionValue: string) => void;
  isVisible: boolean;
  onClose: () => void;
  onOpenLibrary?: () => void;
  className?: string;
  position?: 'above' | 'below';
}

export const PromptSuggestions: React.FC<PromptSuggestionsProps> = ({
  suggestions,
  onSuggestionClick,
  isVisible,
  onClose,
  onOpenLibrary,
  className,
  position = 'above',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, onClose]);

  if (!isVisible || suggestions.length === 0) {
    return null;
  }

  const displayedSuggestions = suggestions.slice(0, 4);

  return (
    <div
      ref={containerRef}
      className={cn(
        'absolute left-0 right-0 z-50',
        'bg-white/95 backdrop-blur-xs',
        'rounded-xl shadow-2xl border border-slate-200/80',
        'p-3 md:p-4 animate-in fade-in-0 duration-300',
        position === 'above' 
          ? 'bottom-full mb-3 slide-in-from-bottom-1' 
          : 'top-full mt-10 slide-in-from-top-1', // Increased margin to appear below disclaimer
        className
      )}
      style={{
        boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.15)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => onOpenLibrary?.()}
            className={cn(
              "p-1.5 bg-slate-100 rounded-lg",
              "hover:bg-slate-200",
              "transition-all duration-200",
              "cursor-pointer"
            )}
            aria-label="Open prompt library"
            title="Open prompt library"
          >
            <Library className="w-4 h-4 text-slate-600" />
          </button>
          <div>
            <h4 className="text-sm font-semibold text-slate-900">
              Prompt Suggestions
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Click to use • Hover for preview
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className={cn(
            "p-1.5 rounded-lg",
            "text-slate-400 hover:text-slate-600",
            "hover:bg-slate-100/80",
            "transition-all duration-200"
          )}
          aria-label="Close suggestions"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Suggestions Grid */}
      <div className="grid grid-cols-2 gap-2">
        <TooltipProvider delayDuration={300}>
          {displayedSuggestions.map((suggestion, index) => (
            <Tooltip key={index}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    onSuggestionClick(suggestion.value);
                    setHoveredIndex(null);
                  }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className={cn(
                    "relative group",
                    "flex items-center gap-2.5",
                    "px-3.5 py-3",
                    "bg-linear-to-br from-slate-50 to-slate-50/50",
                    "border border-slate-200/60",
                    "rounded-lg",
                    "text-left",
                    "transition-all duration-200",
                    "hover:from-blue-50 hover:to-blue-50/30",
                    "hover:border-blue-300/60",
                    "hover:shadow-md hover:-translate-y-0.5",
                    "active:translate-y-0 active:shadow-xs",
                    hoveredIndex === index && "ring-2 ring-blue-400/20"
                  )}
                >
                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm font-medium",
                      "text-slate-800",
                      "capitalize",
                      "transition-colors duration-200",
                      "group-hover:text-blue-900",
                      "line-clamp-2"
                    )}>
                      {suggestion.key}
                    </p>
                  </div>
                </button>
              </TooltipTrigger>
              
              <TooltipContent
                side="top"
                align="start"
                className={cn(
                  "max-w-sm p-3",
                  "bg-slate-900/95 backdrop-blur-xs",
                  "border-slate-700",
                  "animate-in fade-in-0 zoom-in-95 duration-200"
                )}
              >
                <p className="text-xs text-white/90 leading-relaxed">
                  {suggestion.value}
                </p>
              </TooltipContent>
            </Tooltip>
          ))}
        </TooltipProvider>
      </div>
    </div>
  );
};